import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Wrench, Briefcase, UsersThree, ShieldCheck,
  CheckCircle, Spinner, MagnifyingGlass, Plus, Buildings, User as UserIcon,
  Envelope, Phone, HardDrives, PaperPlaneTilt, X, Sparkle,
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";
import LanguagePicker from "@/components/LanguagePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingWizardPage — admin-only multi-step wizard at /admin/onboard.
 *
 * Five steps: role → user details → company link → compressor link → review.
 * Composes existing endpoints — no new backend work required:
 *   • GET    /api/customers?search=...           — find an existing CustomerID
 *   • POST   /api/customers                      — create a brand-new company
 *   • POST   /api/customers/{id}/persons         — append a Persons_for_Customer
 *   • GET    /api/customers/{id}/compressors     — list serials for the link step
 *   • POST   /api/manual/share                   — email the role-specific PDF
 *
 * Staff-tech path skips the company + compressor steps (employees with a
 * company-domain email get auto access on first sign-in via /api/me/can-service-tech).
 */

const ROLES = [
  { id: "staff_tech",    icon: Wrench,     mapsTo: "service_tech", manualRole: "admin",  needsCompany: false },
  { id: "external_tech", icon: Wrench,     mapsTo: "service_tech", manualRole: "dealer", needsCompany: true  },
  { id: "dealer",        icon: UsersThree, mapsTo: "dealer",       manualRole: "dealer", needsCompany: true  },
  { id: "sales_rep",     icon: Briefcase,  mapsTo: "sales",        manualRole: "sales",  needsCompany: false },
];

const STEPS = ["role", "user", "customer", "compressor", "review"];

export default function OnboardingWizardPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();

  const [step, setStep] = useState(STEPS[0]);
  const [roleId, setRoleId] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", phone: "" });
  const [customer, setCustomer] = useState(null);   // {customer_id, company_name, country}
  const [compressor, setCompressor] = useState(null); // {serial_id, serial_number, product_name}
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const role = ROLES.find((r) => r.id === roleId);
  const skipCompany = role && !role.needsCompany;

  const goNext = useCallback(() => {
    const idx = STEPS.indexOf(step);
    let next = STEPS[idx + 1];
    // Skip company + compressor steps for staff-tech / sales-rep
    if (skipCompany && (next === "customer" || next === "compressor")) next = "review";
    if (next) setStep(next);
  }, [step, skipCompany]);

  const goBack = useCallback(() => {
    const idx = STEPS.indexOf(step);
    let prev = STEPS[idx - 1];
    if (skipCompany && (prev === "compressor" || prev === "customer")) prev = "user";
    if (prev) setStep(prev);
  }, [step, skipCompany]);

  const handleFinish = useCallback(async () => {
    if (!role) return;
    setSubmitting(true);
    try {
      // 1) For external roles: ensure Persons_for_Customer link.
      // (Staff/Sales: the customer field is null; we only email the manual.)
      if (customer?.customer_id) {
        await axios.post(`${API}/customers/${encodeURIComponent(customer.customer_id)}/persons`, {
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          phone: userForm.phone.trim(),
          is_primary: false,
          purchasing: false, accounting: false, service: role.mapsTo === "service_tech",
        }, { headers: user?.email ? { "X-User-Email": user.email } : {}, withCredentials: true });
      }
      // 2) Email the role-specific manual PDF.
      await axios.post(`${API}/manual/share`, {
        email: userForm.email.trim(),
        role: role.manualRole,
        lang: (typeof navigator !== "undefined" && (navigator.language || "en").slice(0, 2)) || "en",
        sender_name: user?.name || user?.email || "",
        message: `Welcome aboard! You've been onboarded as ${t(`onboard.role_${role.id}`)}.`,
      }, { headers: user?.email ? { "X-User-Email": user.email } : {}, withCredentials: true });

      setDone(true);
      toast.success(
        t("onboard.success", "User onboarded — manual emailed").replace("{email}", userForm.email),
        { duration: 5000, description: t(`onboard.role_${role.id}`) + (customer ? ` · ${customer.company_name}` : "") }
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  }, [role, customer, userForm, user, t]);

  // Admin gate (all hooks declared above to keep order stable)
  if (user && !user.is_admin && !user.is_super_admin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="onboard-access-denied">
        <div className="text-center">
          <ShieldCheck size={48} weight="duotone" className="mx-auto text-rose-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800">{t("onboard.access_denied")}</h1>
          <button onClick={() => navigate("/")} className="mt-3 text-sm text-blue-600 hover:underline">
            {t("nav.signin")}
          </button>
        </div>
      </div>
    );
  }

  // ─── Header / shell ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="onboard-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="onboard-back-home">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Briefcase size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base sm:text-lg font-black tracking-tighter font-['Chivo'] truncate">{t("onboard.title")}</h1>
          <div className="ml-auto flex items-center gap-2">
            <LanguagePicker />
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
        {/* Progress rail */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 pb-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider">
          {STEPS.map((s, i) => {
            const skip = skipCompany && (s === "customer" || s === "compressor");
            const active = s === step;
            const done = STEPS.indexOf(step) > i && !skip;
            return (
              <div key={s} className="flex items-center gap-1 flex-1" data-testid={`onboard-progress-${s}`}>
                <div className={`flex-1 h-1 rounded-full ${
                  skip ? "bg-slate-100" : active ? "bg-blue-600" : done ? "bg-blue-300" : "bg-slate-200"
                }`} />
                <span className={`whitespace-nowrap hidden sm:inline ${
                  skip ? "text-slate-300 line-through" : active ? "text-blue-700" : done ? "text-blue-400" : "text-slate-400"
                }`}>{t(`onboard.step_${s === "user" ? "user" : s === "customer" ? "customer" : s === "compressor" ? "compressor" : s === "review" ? "review" : "role"}`)}</span>
              </div>
            );
          })}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {done ? (
          <SuccessPanel email={userForm.email} role={role} customer={customer} userForm={userForm} t={t} navigate={navigate} />
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6" data-testid="onboard-subtitle">{t("onboard.subtitle")}</p>

            {step === "role" && (
              <RoleStep selected={roleId} onPick={(id) => { setRoleId(id); goNext(); }} t={t} />
            )}
            {step === "user" && (
              <UserStep form={userForm} setForm={setUserForm} role={role} t={t} />
            )}
            {step === "customer" && (
              <CustomerStep selected={customer} onPick={setCustomer} userForm={userForm} user={user} t={t} />
            )}
            {step === "compressor" && (
              <CompressorStep customer={customer} selected={compressor} onPick={setCompressor} user={user} t={t} />
            )}
            {step === "review" && (
              <ReviewStep role={role} userForm={userForm} customer={customer} compressor={compressor} t={t} />
            )}

            {/* Navigation footer */}
            <div className="mt-8 flex items-center justify-between gap-3">
              {step !== "role" ? (
                <button onClick={goBack} disabled={submitting}
                  className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="onboard-btn-back">
                  <ArrowLeft size={14} weight="bold" /> {t("onboard.back")}
                </button>
              ) : <button onClick={() => navigate("/")} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-sm" data-testid="onboard-btn-cancel">{t("onboard.cancel")}</button>}

              {step !== "role" && step !== "review" && (
                <button onClick={goNext} disabled={!canAdvance(step, { roleId, userForm, customer })}
                  className="px-5 py-2 text-sm font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-sm flex items-center gap-2 transition-colors"
                  data-testid="onboard-btn-next">
                  {t("onboard.next")} <ArrowRight size={14} weight="bold" />
                </button>
              )}
              {step === "review" && (
                <button onClick={handleFinish} disabled={submitting}
                  className="px-5 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-sm flex items-center gap-2 transition-colors"
                  data-testid="onboard-btn-finish">
                  {submitting ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                  {submitting ? t("onboard.creating") : t("onboard.finish")}
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function canAdvance(step, { roleId, userForm, customer }) {
  if (step === "role") return !!roleId;
  if (step === "user") {
    return userForm.name.trim().length > 1 && /^[^@]+@[^@]+\.[^@]+$/.test(userForm.email.trim());
  }
  if (step === "customer") return !!customer?.customer_id;
  if (step === "compressor") return true; // optional
  return true;
}

// ─── Step 1: Role ───────────────────────────────────────────────
function RoleStep({ selected, onPick, t }) {
  const [helperOpen, setHelperOpen] = useState(false);
  const [hEmail, setHEmail] = useState("");
  const [hPosition, setHPosition] = useState("");
  const [thinking, setThinking] = useState(false);

  const askAI = async () => {
    if (!hEmail.trim()) { toast.error(t("onboard.user_email")); return; }
    setThinking(true);
    try {
      const res = await axios.post(`${API}/onboard/suggest-role`, {
        email: hEmail.trim(), position: hPosition.trim(),
      }, { withCredentials: true });
      const { role, rationale, confidence } = res.data || {};
      if (role) {
        onPick(role);
        const roleLabel = t(`onboard.role_${role}`);
        const pct = Math.round((confidence || 0) * 100);
        toast.success(
          t("onboard.help_decide_picked", "Suggested")
            .replace("{role}", roleLabel)
            .replace("{confidence}", pct),
          { description: rationale }
        );
        setHelperOpen(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof detail === "string" ? detail : "Failed");
    } finally {
      setThinking(false);
    }
  };

  return (
    <div data-testid="onboard-step-role">
      <div className="grid sm:grid-cols-2 gap-3">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const active = selected === r.id;
          return (
            <button key={r.id} onClick={() => onPick(r.id)}
              data-testid={`onboard-role-${r.id}`}
              className={`relative text-left p-5 rounded-sm border-2 transition-all ${
                active ? "border-blue-600 bg-blue-50 shadow-md" : "border-slate-200 bg-white hover:border-blue-300 hover:shadow"
              }`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${active ? "bg-blue-600" : "bg-slate-100"}`}>
                  <Icon size={22} weight="fill" className={active ? "text-white" : "text-slate-600"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm">{t(`onboard.role_${r.id}`)}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{t(`onboard.role_${r.id}_desc`)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* AI helper */}
      <div className="mt-4">
        {!helperOpen ? (
          <button onClick={() => setHelperOpen(true)}
            data-testid="onboard-helper-open"
            className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> {t("onboard.help_decide")}
          </button>
        ) : (
          <div className="p-4 rounded-sm border-2 border-dashed border-blue-200 bg-blue-50/30 space-y-3" data-testid="onboard-helper-panel">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-700">
              <Sparkle size={12} weight="fill" /> {t("onboard.help_decide")}
            </div>
            <Field label={t("onboard.help_decide_email")} icon={Envelope}>
              <input type="email" value={hEmail} onChange={(e) => setHEmail(e.target.value)}
                data-testid="onboard-helper-email"
                className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
                placeholder="jane@company.com" />
            </Field>
            <Field label={t("onboard.help_decide_position")} icon={Briefcase}>
              <input type="text" value={hPosition} onChange={(e) => setHPosition(e.target.value)}
                data-testid="onboard-helper-position"
                className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
                placeholder={t("onboard.position_placeholder", "Field Service Technician / Sales Manager / …")} />
            </Field>
            <div className="flex items-center gap-2">
              <button onClick={askAI} disabled={thinking || !hEmail.trim()}
                data-testid="onboard-helper-ask"
                className="px-4 py-2 text-sm font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-sm flex items-center gap-2 transition-colors">
                {thinking ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
                {thinking ? t("onboard.help_decide_thinking") : t("onboard.help_decide_btn")}
              </button>
              <button onClick={() => setHelperOpen(false)} disabled={thinking}
                data-testid="onboard-helper-close"
                className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-sm">
                {t("onboard.help_decide_close")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 2: User ───────────────────────────────────────────────
function UserStep({ form, setForm, role, t }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4" data-testid="onboard-step-user">
      <Field label={t("onboard.user_full_name")} icon={UserIcon}>
        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
          data-testid="onboard-user-name"
          className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          placeholder={t("onboard.name_placeholder", "Jane Doe")} />
      </Field>
      <Field label={t("onboard.user_email")} icon={Envelope}
        hint={role?.id === "staff_tech" || role?.id === "sales_rep" ? t("onboard.staff_email_hint") : null}>
        <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
          data-testid="onboard-user-email"
          className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          placeholder="jane@usaircompressor.com" />
      </Field>
      <Field label={t("onboard.user_phone")} icon={Phone}>
        <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
          data-testid="onboard-user-phone"
          className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          placeholder="+1 555 0100" />
      </Field>
    </div>
  );
}

// ─── Step 3: Customer (search or create inline) ─────────────────
function CustomerStep({ selected, onPick, userForm, user, t }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);   // inline-form expanded
  const [submitting, setSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState({
    company_name: "", country: "US", bill_to_address: "",
  });

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const headers = user?.email ? { "X-User-Email": user.email } : {};
        const us = await axios.get(`${API}/customers?search=${encodeURIComponent(query)}&country=US&per_page=10`, { headers, withCredentials: true });
        let items = us?.data?.items || [];
        if (items.length < 5) {
          const ca = await axios.get(`${API}/customers?search=${encodeURIComponent(query)}&country=CA&per_page=10`, { headers, withCredentials: true });
          items = [...items, ...(ca?.data?.items || [])];
        }
        setResults(items.slice(0, 12));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [query, user]);

  const submitNewCompany = async () => {
    if (!createForm.company_name.trim()) {
      toast.error(t("onboard.new_company_name", "Company name") + " ?");
      return;
    }
    setSubmitting(true);
    try {
      const headers = user?.email ? { "X-User-Email": user.email } : {};
      const res = await axios.post(`${API}/customers`, {
        company_name: createForm.company_name.trim(),
        type: "EndUser",
        country: createForm.country,
        currency: createForm.country === "CA" ? "CAD" : "USD",
        bill_to_address: createForm.bill_to_address.trim(),
        primary_contact: {
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          phone: userForm.phone.trim(),
        },
      }, { headers, withCredentials: true });
      const newId = res?.data?.customer_id;
      if (newId) {
        onPick({ customer_id: newId, company_name: createForm.company_name.trim(), is_new: true });
        toast.success(`${createForm.company_name.trim()} · #${newId}`);
        setCreating(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Inline create form panel ──
  if (creating) {
    return (
      <div className="space-y-4" data-testid="onboard-customer-create-form">
        <div className="text-xs font-bold uppercase tracking-wider text-blue-700">
          {t("onboard.create_new_company")}
        </div>
        <Field label={t("onboard.new_company_name")} icon={Buildings}>
          <input type="text" value={createForm.company_name}
            onChange={(e) => setCreateForm((f) => ({ ...f, company_name: e.target.value }))}
            data-testid="onboard-newco-name"
            className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
            placeholder={t("onboard.company_placeholder", "Acme Industrial Inc.")} />
        </Field>
        <Field label={t("onboard.new_company_country")}>
          <select value={createForm.country}
            onChange={(e) => setCreateForm((f) => ({ ...f, country: e.target.value }))}
            data-testid="onboard-newco-country"
            className="w-full bg-transparent text-sm focus:outline-none">
            <option value="US">{t("onboard.country_us", "United States (USD)")}</option>
            <option value="CA">{t("onboard.country_ca", "Canada (CAD)")}</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>
        <Field label={t("onboard.new_company_address")}>
          <input type="text" value={createForm.bill_to_address}
            onChange={(e) => setCreateForm((f) => ({ ...f, bill_to_address: e.target.value }))}
            data-testid="onboard-newco-address"
            className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
            placeholder={t("onboard.address_placeholder", "123 Industrial Dr, City, State, ZIP")} />
        </Field>
        <div className="flex items-center gap-2 pt-2">
          <button onClick={submitNewCompany} disabled={submitting || !createForm.company_name.trim()}
            data-testid="onboard-newco-save"
            className="px-4 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-sm flex items-center gap-2 transition-colors">
            {submitting ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
            {submitting ? t("onboard.new_company_creating") : t("onboard.new_company_save")}
          </button>
          <button onClick={() => setCreating(false)} disabled={submitting}
            data-testid="onboard-newco-cancel"
            className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-sm">
            {t("onboard.new_company_cancel")}
          </button>
        </div>
        <p className="text-[11px] text-slate-400">
          {t("onboard.user_full_name")}: <strong>{userForm.name || "—"}</strong> ·{" "}
          {t("onboard.user_email")}: <strong>{userForm.email || "—"}</strong> →{" "}
          {t("onboard.success_company_label")}.
        </p>
      </div>
    );
  }

  // ─── Search-and-pick view ──
  return (
    <div className="space-y-4" data-testid="onboard-step-customer">
      <Field label={t("onboard.search_company")} icon={MagnifyingGlass}>
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
          data-testid="onboard-customer-search"
          className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          placeholder={t("onboard.search_placeholder", "Acme Industrial / 4f3a9c1b")} />
      </Field>

      {searching && <div className="text-xs text-slate-400 flex items-center gap-2"><Spinner size={12} className="animate-spin" /> Searching…</div>}

      {results.length === 0 && query.trim().length >= 2 && !searching && (
        <p className="text-xs text-slate-500 italic">{t("onboard.no_results")}</p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {results.map((c) => {
          const id = c.CustomerID || c.customer_id || c.id;
          const name = c["Company Name"] || c.company_name || c.name || "(unnamed)";
          const isPicked = selected?.customer_id === id;
          return (
            <button key={id} onClick={() => onPick({ customer_id: id, company_name: name })}
              data-testid={`onboard-customer-pick-${id}`}
              className={`w-full text-left p-3 rounded-sm border transition-all ${
                isPicked ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
              }`}>
              <div className="flex items-center gap-2">
                <Buildings size={16} weight="bold" className="text-slate-400" />
                <span className="font-semibold text-sm text-slate-800 truncate flex-1">{name}</span>
                <span className="text-[10px] font-mono text-slate-400">#{id}</span>
                {isPicked && <CheckCircle size={16} weight="fill" className="text-emerald-600" />}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => setCreating(true)}
        data-testid="onboard-customer-new-link"
        className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
        <Plus size={12} weight="bold" /> {t("onboard.create_new_company")}
      </button>
    </div>
  );
}

// ─── Step 4: Compressor (optional link) ─────────────────────────
function CompressorStep({ customer, selected, onPick, user, t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer?.customer_id) return;
    (async () => {
      setLoading(true);
      try {
        const headers = user?.email ? { "X-User-Email": user.email } : {};
        const r = await axios.get(`${API}/customers/${encodeURIComponent(customer.customer_id)}/compressors`, { headers, withCredentials: true });
        setItems(r?.data?.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [customer, user]);

  return (
    <div className="space-y-4" data-testid="onboard-step-compressor">
      <p className="text-xs text-slate-500">{t("onboard.compressor_hint")}</p>
      {loading ? (
        <div className="text-xs text-slate-400 flex items-center gap-2"><Spinner size={12} className="animate-spin" /> Loading compressors…</div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">{t("onboard.no_compressors", "No compressors linked to this company yet — that's fine, you can")} {t("onboard.skip_compressor").toLowerCase()}.</p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {items.map((c) => {
            const id = c.serial_id || c.id;
            const isPicked = selected?.serial_id === id;
            return (
              <button key={id} onClick={() => onPick({ serial_id: id, serial_number: c.serial_number, product_name: c.product_name })}
                data-testid={`onboard-compressor-pick-${id}`}
                className={`w-full text-left p-3 rounded-sm border transition-all ${
                  isPicked ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                }`}>
                <div className="flex items-center gap-2">
                  <HardDrives size={16} weight="bold" className="text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-slate-800 truncate">{c.serial_number}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.product_name}</div>
                  </div>
                  {isPicked && <CheckCircle size={16} weight="fill" className="text-emerald-600" />}
                </div>
              </button>
            );
          })}
        </div>
      )}
      <button onClick={() => onPick(null)}
        data-testid="onboard-compressor-skip"
        className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <X size={12} weight="bold" /> {t("onboard.skip_compressor")}
      </button>
    </div>
  );
}

// ─── Step 5: Review ─────────────────────────────────────────────
function ReviewStep({ role, userForm, customer, compressor, t }) {
  return (
    <div className="space-y-3" data-testid="onboard-step-review">
      <p className="text-sm text-slate-600 mb-4">{t("onboard.review_summary")}</p>
      <ReviewRow label={t("onboard.step_role")} value={t(`onboard.role_${role.id}`)} />
      <ReviewRow label={t("onboard.user_full_name")} value={userForm.name} />
      <ReviewRow label={t("onboard.user_email")} value={userForm.email} mono />
      {userForm.phone && <ReviewRow label={t("onboard.user_phone")} value={userForm.phone} mono />}
      {customer && <ReviewRow label={t("onboard.step_customer")} value={`${customer.company_name} · #${customer.customer_id}`} mono />}
      {compressor && <ReviewRow label={t("onboard.step_compressor")} value={`${compressor.serial_number} · ${compressor.product_name || ""}`} mono />}
      <div className="mt-6 p-4 rounded-sm bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
        <PaperPlaneTilt size={16} weight="fill" className="inline-block mr-2 mb-0.5 text-emerald-600" />
        {t("onboard.send_manual")
          .replace("{role}", t(`help.role_${role.manualRole}`, role.manualRole))
          .replace("{email}", userForm.email)}
      </div>
      {/* iter115 — quick "What's new" highlights so onboarding users see
          the most recently-shipped features in a single glance. */}
      <WhatsNewHighlights role={role} t={t} />
    </div>
  );
}

// iter115 — Compact highlights card for newly-shipped features. Uses
// the same i18n key set in EN / FR / ES so onboarding wizards in any
// language see the same callouts. Shows 4 bullets max — recently
// completed user-visible work that an admin / dealer / sales rep
// should know about on day one.
function WhatsNewHighlights({ role, t }) {
  const isInternal = role?.manualRole === "admin" || role?.manualRole === "sales";
  // Common items everyone sees; admin/sales also see 2 admin-only ones.
  const items = [
    { icon: Sparkle,    key: "parts_price_list", testId: "whats-new-parts-price-list" },
    { icon: Sparkle,    key: "missing_parts_quote", testId: "whats-new-missing-parts-quote" },
    ...(isInternal ? [
      { icon: Sparkle, key: "test_company_impersonation", testId: "whats-new-test-company" },
      { icon: Sparkle, key: "email_throttle",  testId: "whats-new-email-throttle" },
    ] : [
      { icon: Sparkle, key: "warranty_policy_self", testId: "whats-new-warranty-policy" },
      { icon: Sparkle, key: "ai_assistant_access", testId: "whats-new-ai-access" },
    ]),
  ];
  return (
    <div className="mt-4 p-4 rounded-sm bg-violet-50 border border-violet-200" data-testid="onboard-whats-new">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={14} weight="fill" className="text-violet-600" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">
          {t("onboard.whats_new_heading", "What's new")}
        </span>
      </div>
      <ul className="space-y-1.5 text-xs text-violet-900">
        {items.map((it) => (
          <li key={it.key} className="flex items-start gap-2" data-testid={it.testId}>
            <it.icon size={11} weight="fill" className="text-violet-500 shrink-0 mt-[3px]" />
            <span>
              <strong>{t(`onboard.whats_new.${it.key}_title`, it.key)}:</strong>{" "}
              {t(`onboard.whats_new.${it.key}_desc`, "")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3 py-2 border-b border-slate-100">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 w-32 shrink-0">{label}</span>
      <span className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

// ─── Success ────────────────────────────────────────────────────
function SuccessPanel({ email, role, customer, userForm, t, navigate }) {
  return (
    <div className="text-center py-12" data-testid="onboard-success">
      <CheckCircle size={64} weight="fill" className="mx-auto text-emerald-500 mb-4" />
      <h2 className="text-xl font-black text-slate-900 mb-1">{t("onboard.success", "Done").replace("{email}", email)}</h2>
      <p className="text-sm text-slate-500 mb-6">
        {userForm?.name || email} · {t(`help.role_${role.manualRole}`, role.manualRole)}
      </p>

      {/* Rich summary card */}
      <div className="max-w-md mx-auto bg-white border border-emerald-200 rounded-sm p-4 text-left mb-6" data-testid="onboard-success-summary">
        <SuccessRow label={t("onboard.success_role_label")} value={t(`onboard.role_${role.id}`)} />
        <SuccessRow label={t("onboard.user_email")} value={email} mono />
        {customer && (
          <SuccessRow label={t("onboard.success_company_label")}
            value={`${customer.company_name} · #${customer.customer_id}`} mono />
        )}
      </div>

      <div className="flex gap-2 justify-center flex-wrap">
        <button onClick={() => window.location.reload()}
          data-testid="onboard-success-another"
          className="px-4 py-2 text-sm font-bold bg-slate-800 hover:bg-slate-900 text-white rounded-sm transition-colors">
          {t("onboard.success_onboard_another")}
        </button>
        {customer && (
          <button onClick={() => navigate(`/customers-us`)}
            data-testid="onboard-success-view-customer"
            className="px-4 py-2 text-sm font-bold border border-slate-300 hover:bg-slate-50 rounded-sm">
            {t("onboard.success_view_customer")}
          </button>
        )}
        <button onClick={() => navigate("/")}
          data-testid="onboard-success-home"
          className="px-4 py-2 text-sm font-bold border border-slate-300 hover:bg-slate-50 rounded-sm">
            {t("nav.account")}
        </button>
      </div>
    </div>
  );
}

function SuccessRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-24 shrink-0">{label}</span>
      <span className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

// ─── Reusable field shell ───────────────────────────────────────
function Field({ label, icon: Icon, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-sm bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200 transition-all">
        {Icon && <Icon size={14} weight="bold" className="text-slate-400 shrink-0" />}
        {children}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}
